/**
 * PaymentsSetup — Admin panel section for PayPal + Bonifico + Stripe toggle.
 */
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/config";
import { toast } from "sonner";
import { Loader2, CreditCard, Landmark, Save, ExternalLink } from "lucide-react";

export default function PaymentsSetup() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/payments/config");
      setCfg(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Errore caricamento"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = JSON.parse(JSON.stringify(cfg));
      // don't send masked secret back
      if (payload.paypal?.secret === "••••••") delete payload.paypal.secret;
      const r = await api.put("/admin/payments/config", payload);
      setCfg(r.data);
      toast.success("Configurazione pagamenti salvata");
    } catch (err) {
      toast.error(errMsg(err, "Salvataggio fallito"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>;
  }

  const patch = (section, key, value) =>
    setCfg({ ...cfg, [section]: { ...cfg[section], [key]: value } });

  return (
    <div data-testid="payments-setup" className="space-y-8 max-w-4xl">
      {/* Stripe */}
      <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <CreditCard size={16} className="text-[#ff4b00]" />
              <div className="font-display text-2xl">Stripe (carta di credito)</div>
            </div>
            <div className="font-mono-tech text-[10px] text-white/40 mt-1">Test mode già configurato tramite chiavi env</div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-[#ff4b00]"
              checked={!!cfg.stripe?.enabled}
              onChange={(e) => patch("stripe", "enabled", e.target.checked)}
              data-testid="pay-stripe-enabled"
            />
            <span className="font-mono-tech text-xs text-white/70">Attivo</span>
          </label>
        </div>
      </section>

      {/* PayPal */}
      <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-display text-2xl text-[#0070ba]">Pay</span>
              <span className="font-display text-2xl text-[#003087]">Pal</span>
            </div>
            <div className="font-mono-tech text-[10px] text-white/40 mt-1">
              Chiavi da <a href="https://developer.paypal.com/dashboard" target="_blank" rel="noreferrer" className="text-[#ff4b00] hover:underline">developer.paypal.com <ExternalLink size={10} className="inline" /></a>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-[#ff4b00]"
              checked={!!cfg.paypal?.enabled}
              onChange={(e) => patch("paypal", "enabled", e.target.checked)}
              data-testid="pay-paypal-enabled"
            />
            <span className="font-mono-tech text-xs text-white/70">Attivo</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Ambiente</label>
            <select
              className="field-boxed mt-2"
              value={cfg.paypal.mode}
              onChange={(e) => patch("paypal", "mode", e.target.value)}
              data-testid="pay-paypal-mode"
            >
              <option value="sandbox">Sandbox (test)</option>
              <option value="live">Live (produzione)</option>
            </select>
          </div>
          <div />
          <div>
            <label className="field-label">Client ID</label>
            <input
              className="field-boxed mt-2 font-mono-tech"
              placeholder="AeA1QIZXi..."
              value={cfg.paypal.client_id || ""}
              onChange={(e) => patch("paypal", "client_id", e.target.value)}
              data-testid="pay-paypal-client-id"
            />
          </div>
          <div>
            <label className="field-label">Secret</label>
            <input
              type="password"
              className="field-boxed mt-2 font-mono-tech"
              placeholder={cfg.paypal.secret === "••••••" ? "••••••" : "EDx3RSLtl..."}
              onChange={(e) => patch("paypal", "secret", e.target.value)}
              data-testid="pay-paypal-secret"
            />
            <div className="font-mono-tech text-[9px] text-white/30 mt-1">
              Il secret è salvato criptato lato server, mai restituito al client.
            </div>
          </div>
        </div>
      </section>

      {/* Bonifico */}
      <section className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3">
              <Landmark size={16} className="text-[#ff4b00]" />
              <div className="font-display text-2xl">Bonifico Bancario</div>
            </div>
            <div className="font-mono-tech text-[10px] text-white/40 mt-1">
              I dati IBAN vengono mostrati al cliente quando sceglie bonifico
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="accent-[#ff4b00]"
              checked={!!cfg.bonifico?.enabled}
              onChange={(e) => patch("bonifico", "enabled", e.target.checked)}
              data-testid="pay-bonifico-enabled"
            />
            <span className="font-mono-tech text-xs text-white/70">Attivo</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Banca</label>
            <input
              className="field-boxed mt-2"
              value={cfg.bonifico.bank || ""}
              onChange={(e) => patch("bonifico", "bank", e.target.value)}
              data-testid="pay-bonifico-bank"
            />
          </div>
          <div>
            <label className="field-label">Intestatario</label>
            <input
              className="field-boxed mt-2"
              value={cfg.bonifico.holder || ""}
              onChange={(e) => patch("bonifico", "holder", e.target.value)}
              data-testid="pay-bonifico-holder"
            />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">IBAN</label>
            <input
              className="field-boxed mt-2 font-mono-tech tracking-wide"
              value={cfg.bonifico.iban || ""}
              onChange={(e) => patch("bonifico", "iban", e.target.value.toUpperCase().replace(/\s/g, ""))}
              data-testid="pay-bonifico-iban"
            />
          </div>
          <div>
            <label className="field-label">BIC / SWIFT (opzionale)</label>
            <input
              className="field-boxed mt-2 font-mono-tech"
              value={cfg.bonifico.bic || ""}
              onChange={(e) => patch("bonifico", "bic", e.target.value.toUpperCase().trim())}
              data-testid="pay-bonifico-bic"
            />
          </div>
          <div>
            <label className="field-label">Causale (template)</label>
            <input
              className="field-boxed mt-2"
              value={cfg.bonifico.causale_template || ""}
              onChange={(e) => patch("bonifico", "causale_template", e.target.value)}
              data-testid="pay-bonifico-causale"
            />
            <div className="font-mono-tech text-[9px] text-white/30 mt-1">
              Variabili: {"{quote_id_short}"} · {"{customer_name}"}
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="pay-save"
          className="btn-primary"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Salva configurazione</>}
        </button>
      </div>
    </div>
  );
}
